import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Divider,
  Chip,
  Stack,
  CircularProgress,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import SchoolIcon from "@mui/icons-material/School";
import { useLanguage } from "../context/LanguageContext";
import { useTeluguYear } from "../context/TeluguYearContext";
import api from "../utils/api";
import Breadcrumb from "../components/Breadcrumb";

export default function ParikshaadhikariDetails() {
  const { lang } = useLanguage();
  const { teluguYearTelugu } = useTeluguYear();

  const [loading, setLoading] = useState(true);
  const [currentExamExaminers, setCurrentExamExaminers] = useState([]);
  const [sanskritExaminers, setSanskritExaminers] = useState([]);
  const [chiefExaminer, setChiefExaminer] = useState(null);

  const labels = {
    telugu: {
      pageTitle: "విశ్వావసు సంవత్సర పరీక్షాధికారులు",
      chiefSupervisor: "ప్రధాన పర్యవేక్షకులు",
      allExamsNote: "అన్ని పరీక్షలు వీరి నిర్వహణలో నిర్వహించబడతాయి",
      allExaminers: "కృష్ణ యజుర్వేద పరీక్షల పరీక్షాధికారులు",
      peopleCount: "జనులు",
      specialExaminers: "విశేష ఘన, క్రమ పరీక్షల పరీక్షాధికారులు",
      chiefExaminer: "ప్రధాన పర్యవేక్షకులు",
      allExamsSupervision: "అన్ని పరీక్షలు",
      supervisedBy: "పర్యవేక్షణలో నిర్వహింపబడును",
      chiefExaminerEnglish: "Chief Examiner",
      specialExamsEnglish: "Special Exams",
      breadcrumbPariksha: "పరీక్ష",
      breadcrumbCurrent: "పరీక్షాధికారులు",
      sanskritExaminersHeading: "సాహిత్య - శాస్త్ర పరీక్షల పరీక్షాధికారులు",
    },
    sanskrit: {
      pageTitle: "विश्वावसु-संवत्सरस्य परीक्षा-अधिकारीणः",
      chiefSupervisor: "प्रधान पर्यवेक्षकाः",
      allExamsNote: "सर्वे परीक्षाः एतेषां व्यवस्थापने सम्पाद्यन्ते",
      allExaminers: "कृष्ण यजुर्वेद परीक्षा परीक्षाधिकारिणः",
      peopleCount: "जनाः",
      specialExaminers: "विशेष घन, क्रम परीक्षाणां परीक्षाधिकारिणः",
      chiefExaminerLabel: "प्रधान पर्यवेक्षकाः",
      allExamsSupervision: "सर्वे परीक्षाः",
      supervisedBy: "पर्यवेक्षणे सम्पाद्यन्ते",
      chiefExaminerEnglish: "Chief Examiner",
      specialExamsEnglish: "Special Exams",
      breadcrumbPariksha: "परीक्षा",
      breadcrumbCurrent: "परीक्षाधिकारिणः",
      sanskritExaminersHeading: "साहित्य - शास्त्र परीक्षा परीक्षाधिकारिणः",
    },
  };

  const t = labels[lang];

  const breadcrumbItems = [
    { label: t.breadcrumbPariksha, path: "/parikshaMgmt" },
    { label: t.breadcrumbCurrent, path: null }
  ];

  const specialExaminers = [
    { telugu: "మద్దులపల్లి సూర్యనారాయణ సలక్షణ ఘనపాఠీ గారు", sanskrit: "श्री मद्दुलपल्ली सूर्यनारायण सलक्षण घनपाठी" },
    { telugu: "కూచిభట్ల తారక రామ కుమార ఘనపాఠీ గారు", sanskrit: "श्री कूचिभट्ल तारक राम कुमार् घनपाठी" },
    { telugu: "విష్ణుభట్ల సుబ్రహ్మణ్యేశ్వర సలక్షణ ఘనపాఠీ (కన్నయ్య) గారు", sanskrit: "श्री विष्णुभट्ल सुब्रह्मण्येश्वर सलक्षण (कन्नय्य) घनपाती" },
    { telugu: "దెందుకూరి శ్రీ రామచంద్ర ఘనపాఠీ సోమయాజులు గారు", sanskrit: "श्री देन्दुकुरी श्री रामचंद्र घनपाठी सोमयाजुलु" },
  ];

  // Fetch examiners data from API
  useEffect(() => {
    const fetchExaminers = async () => {
      if (!teluguYearTelugu) return;

      setLoading(true);
      try {
        const res = await api.get(`/api/parikshaadhikari/year/${teluguYearTelugu}`);
        const data = await res.json();

        if (res.ok && data.success && Array.isArray(data.data)) {
          const examiners = data.data;

          // Filter examiners by pariksha_types
          const krishnaYajurvedaExaminers = examiners.filter(examiner => 
            examiner.pariksha_types && examiner.pariksha_types.includes('krishna_yajurveda')
          );

          const sanskritParikshaExaminers = examiners.filter(examiner => 
            examiner.pariksha_types && examiner.pariksha_types.includes('sanskrit')
          );

          // Set chief examiner
          const chief = examiners.find(e => e.specialExaminer);
          if (chief) {
            setChiefExaminer({
              telugu: chief.telugu,
              sanskrit: chief.sanskrit
            });
          }

          // Map examiners to required format
          setCurrentExamExaminers(krishnaYajurvedaExaminers.map(e => ({
            telugu: e.telugu,
            sanskrit: e.sanskrit
          })));

          setSanskritExaminers(sanskritParikshaExaminers.map(e => ({
            telugu: e.telugu,
            sanskrit: e.sanskrit
          })));
        }
      } catch (err) {
        console.error('Error fetching examiners:', err);
        // Keep empty arrays on error
      } finally {
        setLoading(false);
      }
    };

    fetchExaminers();
  }, [teluguYearTelugu]);

  const ExaminerListItem = ({
    number,
    name,
    isChief = false,
    isSpecial = false,
    labels,
  }) => {
    const { lang: itemLang } = useLanguage();
    const displayName =
      typeof name === "string"
        ? name
        : itemLang === "telugu"
        ? name.telugu
        : name.sanskrit;
    const fontFamily =
      itemLang === "telugu"
        ? '"Noto Sans Telugu", sans-serif'
        : '"Noto Sans Devanagari", "Noto Sans", sans-serif';

    return (
      <Paper
        elevation={isChief ? 6 : isSpecial ? 3 : 2}
        sx={{
          padding: "20px 24px",
          borderRadius: "12px",
          background: isChief
            ? "linear-gradient(135deg, #7f4b2d 0%, #b06b3a 100%)"
            : isSpecial
            ? "linear-gradient(135deg, #003080 0%, #7f4b2d 100%)"
            : "#fff",
          color: isChief || isSpecial ? "#fff" : "#7f4b2d",
          display: "flex",
          alignItems: "center",
          gap: "20px",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          border: isChief
            ? "2px solid #FFD700"
            : isSpecial
            ? "1px solid #E0E0E0"
            : "1px solid #E8EAF6",
          position: "relative",
          overflow: "hidden",
          "&:hover": {
            transform: isChief ? "translateY(-6px)" : "translateY(-3px)",
            boxShadow: isChief
              ? "0 12px 24px rgba(0, 32, 96, 0.35)"
              : isSpecial
              ? "0 8px 16px rgba(0, 32, 96, 0.2)"
              : "0 6px 12px rgba(0, 32, 96, 0.15)",
          },
          "&::before": isChief
            ? {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "radial-gradient(circle at top right, rgba(255, 215, 0, 0.15), transparent)",
                pointerEvents: "none",
              }
            : {},
        }}
      >
        {/* Badge/Circle Number */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "56px",
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            background: isChief || isSpecial ? "#fff" : "#7f4b2d",
            color: isChief || isSpecial ? "#7f4b2d" : "#fff",
            fontWeight: "bold",
            fontSize: "20px",
            boxShadow: "0 3px 8px rgba(0, 0, 0, 0.2)",
            position: "relative",
          }}
        >
          {number}
          {isChief && (
            <Box
              sx={{
                position: "absolute",
                top: "-8px",
                right: "-8px",
                fontSize: "20px",
                color: "#FFD700",
                background: "#7f4b2d",
                borderRadius: "50%",
                padding: "2px",
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
              }}
            >
              ॐ
            </Box>
          )}
        </Box>

        {/* Text Content */}
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: isChief ? "bold" : isSpecial ? "600" : "500",
              lineHeight: "1.6",
              fontFamily,
              letterSpacing: "0.3px",
            }}
          >
            {displayName}
          </Typography>
          {isChief && (
            <Typography
              sx={{
                fontSize: "12px",
                opacity: 0.9,
                marginTop: "4px",
                fontFamily: '"Noto Sans", sans-serif',
                fontWeight: "500",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {labels.chiefExaminerEnglish}
            </Typography>
          )}
          {isSpecial && (
            <Chip
              icon={<VerifiedIcon sx={{ color: "#fff !important" }} />}
              label={labels.specialExamsEnglish}
              size="small"
              sx={{
                marginTop: "6px",
                background: "rgba(255, 255, 255, 0.25)",
                color: "#fff",
                fontWeight: "600",
                height: "24px",
                "& .MuiChip-icon": {
                  color: "#fff",
                },
              }}
            />
          )}
        </Box>
      </Paper>
    );
  };

  // Show loading spinner while fetching data
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "linear-gradient(180deg, #fffaf1 0%, #e9ecf1 100%)",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: "linear-gradient(180deg, #fffaf1 0%, #e9ecf1 100%)",
        minHeight: "100vh",
        paddingY: "50px",
      }}
    >
      <Container maxWidth="lg">
        {/* Breadcrumb Navigation */}
        <Breadcrumb items={breadcrumbItems} />

        {/* Page Title */}
        <Box sx={{ textAlign: "center", marginBottom: "40px" }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "800",
              color: "#7f4b2d",
              marginBottom: "12px",
              fontSize: { xs: "20px", sm: "30px", md: "30px" },
              fontFamily: '"Noto Sans Telugu", sans-serif',
              letterSpacing: "-0.5px",
              display: 'inline-block',
            }}
          >
            <span style={{ borderBottom: '3px solid #7f4b2d', display: 'inline-block' }}>{t.pageTitle}</span>
          </Typography>
        </Box>

        {/* Chief Examiner Featured Section */}
        {chiefExaminer && (
          <Box sx={{ marginBottom: "60px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "24px",
              }}
            >
              <SchoolIcon sx={{ fontSize: "25px", color: "#7f4b2d" }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "700",
                  color: "#7f4b2d",
                  fontSize: "18px",
                  fontFamily: '"Noto Sans Telugu", sans-serif',
                }}
              >
                {t.chiefSupervisor}
              </Typography>
            </Box>
            <ExaminerListItem
              number="ॐ"
              name={chiefExaminer}
              isChief={true}
              labels={t}
            />
            <Typography
              sx={{
                fontSize: "14px",
                color: "#666",
                marginTop: "16px",
                textAlign: "center",
                fontStyle: "italic",
                fontFamily: '"Noto Sans Telugu", sans-serif',
              }}
            >
              {t.allExamsNote}
            </Typography>
          </Box>
        )}

        <Divider
          sx={{
            my: "50px",
            borderColor: "#7f4b2d",
            opacity: 0.1,
            borderWidth: "2px",
          }}
        />

        {/* All Examiners Section */}
        {currentExamExaminers.length > 0 && (
          <Box sx={{ marginBottom: "60px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "32px",
              }}
            >
              <Box
                sx={{
                  width: "8px",
                  height: "32px",
                  background: "linear-gradient(180deg, #7f4b2d 0%, #003080 100%)",
                  borderRadius: "4px",
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "700",
                  color: "#7f4b2d",
                  fontSize: "22px",
                  fontFamily: '"Noto Sans Telugu", sans-serif',
                }}
              >
                {t.allExaminers}
              </Typography>
              <Chip
                label={`${currentExamExaminers.length} ${t.peopleCount}`}
                sx={{
                  background: "#E3F2FD",
                  color: "#7f4b2d",
                  fontWeight: "600",
                  marginLeft: "auto",
                }}
              />
            </Box>

            <Stack spacing={2}>
              {currentExamExaminers.map((examiner, index) => (
                <ExaminerListItem
                  key={index}
                  number={index + 1}
                  name={examiner}
                  isChief={false}
                  labels={t}
                />
              ))}
            </Stack>
          </Box>
        )}
{sanskritExaminers.length > 0 && (
          <Box sx={{ marginBottom: "50px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginBottom: "32px",
                gap: "12px",
              }}
            >
              <Box
                sx={{
                  width: "6px",
                  height: "40px",
                  background: "linear-gradient(180deg, #7f4b2d 0%, #003080 100%)",
                  borderRadius: "4px",
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "700",
                  color: "#7f4b2d",
                  fontSize: { xs: "20px", sm: "24px" },
                  fontFamily: '"Noto Sans Telugu", sans-serif',
                }}
              >
                {t.sanskritExaminersHeading}
              </Typography>
            </Box>

            <Stack spacing={2}>
              {sanskritExaminers.map((examiner, index) => (
                <ExaminerListItem
                  key={index}
                  number={index + 1}
                  name={examiner}
                  labels={t}
                />
              ))}
            </Stack>
          </Box>
        )}

        <Divider sx={{ my: "40px", borderColor: "#7f4b2d", opacity: 0.3 }} />

        {/* Special Examiners Section */}
        <Box sx={{ marginBottom: "50px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: "30px",
              gap: "12px",
            }}
          >
            <Box
              sx={{
                width: "6px",
                height: "40px",
                background: "linear-gradient(180deg, #7f4b2d 0%, #003080 100%)",
                borderRadius: "4px",
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: "#7f4b2d",
                fontSize: { xs: "20px", sm: "24px" },
                fontFamily: '"Noto Sans Telugu", sans-serif',
              }}
            >
              {t.specialExaminers}
            </Typography>
          </Box>

          <Stack spacing={2}>
            {specialExaminers.map((examiner, index) => (
              <ExaminerListItem
                key={index}
                number={index + 1}
                name={examiner}
                isSpecial={true}
                labels={t}
              />
            ))}
          </Stack>
        </Box>

        {/* Supervision Note Section */}
        <Paper
          elevation={4}
          sx={{
            background: "linear-gradient(135deg, #7f4b2d 0%, #003080 100%)",
            color: "#fff",
            padding: { xs: "24px", sm: "32px" },
            borderRadius: "12px",
            marginTop: "40px",
            textAlign: "center",
            border: "2px solid #FFD700",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "radial-gradient(circle at top right, rgba(255, 215, 0, 0.1), transparent)",
              pointerEvents: "none",
            },
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                marginBottom: "8px",
                fontSize: "18px",
                color: "#FFD700",
                fontFamily: '"Noto Sans Telugu", sans-serif',
              }}
            >
              ॐ {t.chiefExaminerLabel || t.chiefSupervisor}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "18px",
                lineHeight: "1.8",
                fontWeight: "500",
                fontFamily: '"Noto Sans Telugu", sans-serif',
              }}
            >
              {t.allExamsSupervision}
              <Typography
                component="span"
                sx={{
                  display: "block",
                  marginY: "12px",
                  fontSize: "20px",
                  fontWeight: "bold",
                  color: "#FFD700",
                  fontFamily: '"Noto Sans Telugu", sans-serif',
                }}
              >
                {chiefExaminer ? (lang === "telugu" ? chiefExaminer.telugu : chiefExaminer.sanskrit) :
                  (lang === "telugu"
                    ? "మద్దులపల్లి సూర్యనారాయణ సలక్షణ ఘనపాఠీ గారి"
                    : "श्री मद्दुलपल्लि सूर्यनारायण सलक्षण घनपाठी")}
              </Typography>
              {t.supervisedBy}
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
